import { parse } from '@babel/parser';
import traverse from '@babel/traverse';
import * as t from '@babel/types';

/**
 * Edits one field of one list record, found by its id (hot edit plan, phase 1).
 *
 * Prototypes keep list data in `src/data/*.ts` as arrays of records with a unique `id`, and render
 * each row with `key={item.id}` (Chef's `DATA_CONVENTION_RULES`). The preview reports the clicked
 * row's React key, which is then the record's id: the record is found in the data files directly,
 * whatever hooks, context, filters, sorting or pagination sit between the data and the list.
 *
 * Only the field's string literal is replaced, so the diff is that one value. Nothing is guessed:
 * no record, two records with the id, or a field that is not a plain string all refuse.
 */
export type DataRecordPatchResult =
  | { ok: true; filePath: string; content: string }
  | { ok: false; error: string };

/**
 * Where a prototype keeps its records. Chef asks for `src/data/*.ts`, but its spec-built
 * prototypes put them in `src/mockData.ts` instead - and on those the founder's list was not
 * editable at all, because this pattern had never heard of the second shape.
 */
export const DATA_FILE_PATTERN = /^src\/(data\/[^/]+|(?:[^/]+\/)*mockData)\.(ts|tsx|js|jsx)$/i;

type Match = { filePath: string; source: string; record: t.ObjectExpression };

/**
 * The startup's shared records, copied in by Chef for the preview (chef `appDataLayer.ts`). Chef
 * rewrites the copy every turn and the preview reads the live records instead (US 596), so an
 * edit here would change nothing anyone sees and vanish on the next turn: a row of shared data
 * is edited in the live data (`liveRecordEdit` in jsxPatcher), or in the Data view.
 */
export const SHARED_DATA_FILE = 'src/data/startupSamples.ts';
export const SHARED_DATA_ERROR =
  "This row is your startup's shared data, which every prototype uses. Change it in the Data view.";

/** Written by Chef into prototypes on the shared data layer, whose lists are live data. */
export const APP_STORE_FILE = 'src/lib/appStore.ts';

export function patchDataRecordById(opts: {
  recordId: string;
  field: string;
  newText: string;
  /** Every workspace path; the data files are picked from it. */
  filePaths: readonly string[];
  readFile: (path: string) => string | null;
}): DataRecordPatchResult {
  const matches: Match[] = [];
  let shared = false;
  for (const filePath of opts.filePaths) {
    const path = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!DATA_FILE_PATTERN.test(path)) continue;
    const source = opts.readFile(filePath);
    if (!source) continue;
    const records = recordsWithId(source, opts.recordId);
    if (path === SHARED_DATA_FILE) {
      shared ||= records.length > 0;
      continue;
    }
    for (const record of records) matches.push({ filePath, source, record });
  }

  if (matches.length === 0 && shared) return { ok: false, error: SHARED_DATA_ERROR };
  if (matches.length === 0) {
    return { ok: false, error: `No record with id "${opts.recordId}" in src/data. Ask Ideoz to change it instead.` };
  }
  if (matches.length > 1) {
    return {
      ok: false,
      error: `More than one record has id "${opts.recordId}", so Hot edit can't tell which to change. Ask Ideoz instead.`,
    };
  }

  const { filePath, source, record } = matches[0]!;
  const prop = record.properties.find(
    (p): p is t.ObjectProperty => t.isObjectProperty(p) && !p.computed && propName(p) === opts.field,
  );
  if (!prop) return { ok: false, error: `This record has no "${opts.field}" field. Ask Ideoz to change it instead.` };
  const value = prop.value;

  // A number or a yes/no in the data file is as editable as a word: the founder typing over
  // "14" in the preview meant the quantity, and telling them to ask Ideoz for that was a wall
  // in front of the simplest change there is. What is written back keeps the field's own type.
  const negative = t.isUnaryExpression(value) && value.operator === '-' && t.isNumericLiteral(value.argument);
  if (t.isNumericLiteral(value) || negative) {
    const typed = opts.newText.trim().replace(/,/g, '');
    const asNumber = Number(typed);
    if (typed === '' || !Number.isFinite(asNumber)) {
      return { ok: false, error: `"${opts.field}" is a number in the data file, so it needs a number.` };
    }
    return {
      ok: true,
      filePath,
      content: source.slice(0, value.start!) + String(asNumber) + source.slice(value.end!),
    };
  }
  if (t.isBooleanLiteral(value)) {
    const typed = opts.newText.trim().toLowerCase();
    const yes = ['true', 'yes', 'on'].includes(typed);
    const no = ['false', 'no', 'off'].includes(typed);
    if (!yes && !no) {
      return { ok: false, error: `"${opts.field}" is a yes or no in the data file, so it needs true or false.` };
    }
    return {
      ok: true,
      filePath,
      content: source.slice(0, value.start!) + String(yes) + source.slice(value.end!),
    };
  }

  if (!t.isStringLiteral(value) && !t.isTemplateLiteral(value)) {
    return { ok: false, error: `"${opts.field}" is not plain text in the data file. Ask Ideoz to change it instead.` };
  }
  if (t.isTemplateLiteral(value) && value.expressions.length > 0) {
    return { ok: false, error: `"${opts.field}" is built from other values. Ask Ideoz to change it instead.` };
  }

  const raw = source.slice(value.start!, value.end!);
  const quote = raw[0] === "'" || raw[0] === '`' ? raw[0] : '"';
  const content = source.slice(0, value.start!) + quoteString(opts.newText, quote) + source.slice(value.end!);
  return { ok: true, filePath, content };
}

function recordsWithId(source: string, recordId: string): t.ObjectExpression[] {
  let ast;
  try {
    ast = parse(source, { sourceType: 'module', plugins: ['jsx', 'typescript'] });
  } catch {
    return [];
  }
  const found: t.ObjectExpression[] = [];
  traverse(ast, {
    ObjectExpression(path) {
      const idProp = path.node.properties.find(
        (p): p is t.ObjectProperty => t.isObjectProperty(p) && !p.computed && propName(p) === 'id',
      );
      if (!idProp) return;
      const v = idProp.value;
      const id = t.isStringLiteral(v) ? v.value : t.isNumericLiteral(v) ? String(v.value) : null;
      if (id === recordId) found.push(path.node);
    },
  });
  return found;
}

function propName(p: t.ObjectProperty): string | null {
  if (t.isIdentifier(p.key)) return p.key.name;
  if (t.isStringLiteral(p.key)) return p.key.value;
  return null;
}

export function quoteString(text: string, quote: string): string {
  const escaped = text
    .replace(/\\/g, '\\\\')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, quote === '`' ? '\n' : '\\n')
    .split(quote)
    .join(`\\${quote}`);
  return quote === '`' ? `\`${escaped.replace(/\$\{/g, '\\${')}\`` : `${quote}${escaped}${quote}`;
}
